// Incentives & payouts: calculate a month, see each incentive's full
// breakdown, move it through manager → finance → admin → paid (one at a time
// or in bulk), adjust with a reason, close the month. Buttons show only what
// THIS user may do; the server checks again (engine.workflowProblem).
import { Fragment, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Calculator, CheckCircle2, ChevronDown, ChevronRight, Lock, LockOpen, SlidersHorizontal, Wallet } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import {
  errText, inr, monthName, perfApi, pctText, STATUS_LABEL, STATUS_TONE, useIncentives, usePerfMutation, type Incentive, type Overview,
} from '../api';
import { Chip, Dialog, Empty, Field, Initials, inputCls, Panel, SelectPill } from './ui';

type Who = 'manage' | 'finance' | 'admin' | 'either';
type ActionDef = { action: string; label: string; from: string[]; who: Who; needs?: 'reason' | 'ref'; style: 'primary' | 'plain' | 'danger' };
const ACTIONS: ActionDef[] = [
  { action: 'submit', label: 'Send for review', from: ['calculated', 'rejected', 'on_hold'], who: 'manage', style: 'primary' },
  { action: 'manager_approve', label: 'Manager approve', from: ['under_review'], who: 'manage', style: 'primary' },
  { action: 'finance_approve', label: 'Finance verify', from: ['manager_approved'], who: 'finance', style: 'primary' },
  { action: 'approve', label: 'Final approve', from: ['finance_approved'], who: 'admin', style: 'primary' },
  { action: 'mark_paid', label: 'Mark paid', from: ['approved'], who: 'finance', needs: 'ref', style: 'primary' },
  { action: 'hold', label: 'Hold', from: ['calculated', 'under_review', 'manager_approved', 'finance_approved', 'approved'], who: 'either', needs: 'reason', style: 'plain' },
  { action: 'reject', label: 'Reject', from: ['under_review', 'manager_approved', 'finance_approved', 'approved'], who: 'either', needs: 'reason', style: 'danger' },
];
const PIPELINE = ['calculated', 'under_review', 'manager_approved', 'finance_approved', 'approved', 'paid'];
const btn = (s: ActionDef['style']) => (s === 'primary' ? 'btn-sp-primary' : s === 'danger' ? 'btn-sp-danger' : 'btn-sp');

export function IncentivesPanel({ fy, month: initialMonth, months, closedMonths, access }: {
  fy: string; month: string; months: string[]; closedMonths: string[]; access: Overview['access'];
}) {
  const [month, setMonth] = useState(initialMonth || months[months.length - 1] || '');
  const [status, setStatus] = useState('');
  const { data, isLoading } = useIncentives({ fy, month });
  const calc = usePerfMutation((m: string) => perfApi.calculate(m));
  const close = usePerfMutation((m: string) => perfApi.closeMonth(m));
  const reopen = usePerfMutation((v: { month: string; reason: string }) => perfApi.reopenMonth(v.month, v.reason));
  const [reopening, setReopening] = useState(false);
  const [reopenWhy, setReopenWhy] = useState('');
  const act = usePerfMutation((v: { id: string; action: string; reason?: string; transactionRef?: string }) => perfApi.action(v.id, v.action, v));
  const adjust = usePerfMutation((v: { id: string; label: string; amount: number; reason: string }) => perfApi.adjust(v.id, v));
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [pending, setPending] = useState<{ def: ActionDef; items: Incentive[] } | null>(null);
  const [adjusting, setAdjusting] = useState<Incentive | null>(null);
  const [closing, setClosing] = useState(false);
  const all = data?.items || [];
  const items = status ? all.filter((i) => i.status === status) : all;
  const isClosed = closedMonths.includes(month);
  const may = (who: Who) => ({ manage: access.manage, finance: access.finance, admin: access.admin, either: access.manage || access.finance }[who]);
  const actionsFor = (inc: Incentive) => ACTIONS.filter((a) => a.from.includes(inc.status) && may(a.who));
  const pickedItems = all.filter((i) => picked.includes(i._id));
  const bulkActions = pickedItems.length ? ACTIONS.filter((a) => may(a.who) && pickedItems.every((i) => a.from.includes(i.status))) : [];

  const sum = (list: Incentive[]) => list.reduce((n, i) => n + i.finalAmount, 0);
  const byStatus = (s: string) => all.filter((i) => i.status === s);

  const run = async (def: ActionDef, list: Incentive[], extra: { reason?: string; transactionRef?: string } = {}) => {
    let ok = 0; const failed: string[] = [];
    for (const inc of list) {
      try { await act.mutateAsync({ id: inc._id, action: def.action, ...extra }); ok += 1; } catch (err) { failed.push(`${inc.employeeName}: ${errText(err, 'failed')}`); }
    }
    if (ok) toast.success(list.length === 1 ? `${list[0].employeeName}: ${def.label.toLowerCase()} done.` : `${def.label}: ${ok} done.`);
    if (failed.length) toast.error(failed.slice(0, 3).join(' · '), { duration: 8000 });
    setPicked([]); setPending(null);
  };
  const start = (def: ActionDef, list: Incentive[]) => setPending({ def, items: list });

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200/80 bg-white p-3">
        <SelectPill label="Month" value={month} onChange={(v) => { setMonth(v); setPicked([]); }} allLabel={`All months of FY ${fy}`}
          options={months.map((k) => ({ value: k, label: `${monthName(k)}${closedMonths.includes(k) ? ' · closed' : ''}` }))} />
        {access.manage && month && (
          <div className="flex gap-2">
            <button type="button" className="btn-sp-primary" disabled={calc.isPending} onClick={async () => {
              try {
                const r = await calc.mutateAsync(month) as { created: number; recalculated: number; locked: number; noTarget: number };
                toast.success(`${monthName(month)} calculated — ${r.created} new, ${r.recalculated} updated, ${r.locked} already in approval (left alone), ${r.noTarget} without a target.`, { duration: 8000 });
              } catch (err) { toast.error(errText(err, 'Could not calculate.')); }
            }}><Calculator className="h-4 w-4" /> {calc.isPending ? 'Calculating…' : `Calculate ${monthName(month)}`}</button>
            {!isClosed ? <button type="button" className="btn-sp" onClick={() => setClosing(true)}><Lock className="h-4 w-4" /> Close month</button>
              : <button type="button" className="btn-sp" onClick={() => setReopening(true)} title="This month is closed and frozen"><LockOpen className="h-4 w-4" /> Reopen month</button>}
          </div>
        )}
        <div className="ml-auto grid grid-cols-3 gap-4 text-right">
          <Total label="Total" value={sum(all)} />
          <Total label="Paid" value={sum(byStatus('paid'))} tone="text-emerald-600" />
          <Total label="Pending" value={sum(all.filter((i) => !['paid', 'rejected'].includes(i.status)))} tone="text-amber-600" />
        </div>
      </div>

      {/* Approval pipeline */}
      {all.length > 0 && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
          {[...PIPELINE, 'on_hold', 'rejected'].filter((s) => s !== 'rejected' || byStatus(s).length).slice(0, 8).map((s, i) => {
            const list = byStatus(s);
            return (
              <button key={s} type="button" onClick={() => setStatus(status === s ? '' : s)}
                className={`relative rounded-xl border bg-white px-3 py-2.5 text-left transition ${status === s ? 'border-blue-500 ring-2 ring-blue-500/15' : 'border-slate-200/80 hover:border-slate-300'}`}>
                <p className="flex items-center gap-1 text-data-xs font-semibold uppercase tracking-wide text-slate-500">{i < PIPELINE.length && <span className="text-slate-300">{i + 1}.</span>}{STATUS_LABEL[s]}</p>
                <p className="mt-0.5 text-lg font-bold text-slate-900">{list.length}</p>
                <p className="text-data-xs tabular-nums text-slate-500">{inr(sum(list))}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Bulk bar */}
      {picked.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50/70 px-4 py-2.5">
          <span className="text-sm font-semibold text-blue-900">{picked.length} selected · {inr(sum(pickedItems))}</span>
          {bulkActions.length === 0 && <span className="text-xs text-blue-800">Pick incentives at the same stage to act on them together.</span>}
          <div className="ml-auto flex flex-wrap gap-2">
            {bulkActions.map((a) => <button key={a.action} type="button" className={btn(a.style)} onClick={() => start(a, pickedItems)}>{a.label}</button>)}
            <button type="button" className="btn-sp" onClick={() => setPicked([])}>Clear</button>
          </div>
        </div>
      )}

      {isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : items.length === 0 ? (
        <Empty title={`No incentives ${month ? `for ${monthName(month)}` : 'yet'}${status ? ` in "${STATUS_LABEL[status]}"` : ''}`}
          text={access.manage && month && !status ? `Press "Calculate ${monthName(month)}" to work them out from that month's sales. Until then the dashboard shows projections.` : 'The dashboard shows projected figures until a month is calculated.'} />
      ) : (
        <Panel pad={false} title="Incentives" subtitle="Open a row to see exactly how it was calculated and who approved it" icon={Wallet}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-10 px-3 py-2.5">
                  <input type="checkbox" aria-label="Select all" checked={items.length > 0 && items.every((i) => picked.includes(i._id))}
                    onChange={(e) => setPicked(e.target.checked ? items.map((i) => i._id) : [])} />
                </th>
                {['Employee', 'Month', 'Sales', 'Achievement', 'Base', 'Bonuses', 'Deductions', 'Final', 'Status', ''].map((h, i) => <th key={i} className={`whitespace-nowrap px-3 py-2.5 ${['Sales', 'Base', 'Bonuses', 'Deductions', 'Final'].includes(h) ? 'text-right' : ''}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {items.map((inc) => {
                  // Nothing earned and no target: nothing to approve.
                  const nothing = inc.status === 'calculated' && !inc.finalAmount;
                  const acts = nothing ? [] : actionsFor(inc);
                  const canAdjust = access.manage && ['calculated', 'under_review', 'on_hold', 'rejected'].includes(inc.status);
                  const isOpen = open === inc._id;
                  return (
                    <Fragment key={inc._id}>
                      <tr className={`border-t border-slate-100 ${isOpen ? 'bg-blue-50/40' : 'hover:bg-slate-50/70'}`}>
                        <td className="px-3 py-2.5"><input type="checkbox" aria-label={`Select ${inc.employeeName}`} checked={picked.includes(inc._id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, inc._id] : p.filter((x) => x !== inc._id)))} /></td>
                        <td className="px-3 py-2.5">
                          <button type="button" onClick={() => setOpen(isOpen ? null : inc._id)} className="flex items-center gap-2 text-left">
                            {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                            <Initials name={inc.employeeName} size={28} />
                            <span className="font-semibold text-slate-900">{inc.employeeName}</span>
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{monthName(inc.periodKey)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{inr(inc.metrics?.grossSales)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{pctText(inc.metrics?.achievementPct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{inr(inc.base)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{inc.bonuses ? inr(inc.bonuses) : '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-rose-700">{inc.deductions ? inr(inc.deductions) : '—'}</td>
                        <td className="px-3 py-2.5 text-right font-bold tabular-nums text-slate-900">{inr(inc.finalAmount)}</td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          {nothing ? <Chip className="bg-slate-100 text-slate-500">{inc.metrics?.target ? 'Nothing earned' : 'No target'}</Chip> : <Chip className={STATUS_TONE[inc.status] || ''}>{STATUS_LABEL[inc.status] || inc.status}</Chip>}
                          {inc.paidAt && <p className="mt-0.5 text-data-xs text-slate-500">{new Date(inc.paidAt).toLocaleDateString('en-IN')} · {inc.transactionRef}</p>}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex justify-end gap-1.5">
                            {acts.filter((a) => a.style === 'primary').map((a) => <button key={a.action} type="button" className="btn-sp-primary !px-2.5 !py-1 !text-xs" onClick={() => start(a, [inc])}>{a.label}</button>)}
                            {(acts.some((a) => a.style !== 'primary') || canAdjust) && (
                              <RowMenu actions={acts.filter((a) => a.style !== 'primary')} canAdjust={canAdjust} onAction={(a) => start(a, [inc])} onAdjust={() => setAdjusting(inc)} />
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && <tr className="bg-blue-50/30"><td /><td colSpan={10} className="px-3 pb-4 pt-1"><Breakdown inc={inc} /></td></tr>}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {pending && <ActionDialog def={pending.def} items={pending.items} busy={act.isPending} onClose={() => setPending(null)} onConfirm={(extra) => run(pending.def, pending.items, extra)} />}
      {adjusting && <AdjustDialog inc={adjusting} busy={adjust.isPending} onClose={() => setAdjusting(null)} onSave={async (v) => {
        try { await adjust.mutateAsync({ id: adjusting._id, ...v }); toast.success('Adjustment saved — the calculated amount stays in the history.'); setAdjusting(null); }
        catch (err) { toast.error(errText(err, 'Could not adjust.')); }
      }} />}
      <Dialog open={reopening} onClose={() => setReopening(false)} title={`Reopen ${monthName(month)}?`}
        subtitle="The frozen figures are removed and the month shows live figures again. Incentives already in approval are not changed."
        footer={(
          <>
            <button type="button" className="btn-sp" onClick={() => setReopening(false)}>Cancel</button>
            <button type="button" className="btn-sp-primary" disabled={reopen.isPending || !reopenWhy.trim()} onClick={async () => {
              try { await reopen.mutateAsync({ month, reason: reopenWhy }); toast.success(`${monthName(month)} reopened. Close it again when the corrections are done.`); setReopening(false); setReopenWhy(''); }
              catch (err) { toast.error(errText(err, 'Could not reopen the month.')); }
            }}><LockOpen className="h-4 w-4" /> {reopen.isPending ? 'Reopening…' : 'Reopen'}</button>
          </>
        )}>
        <Field label="Why is it being reopened?" hint="Kept in the audit log. The month will not close automatically again — close it here when ready."><textarea className={inputCls} rows={2} autoFocus value={reopenWhy} onChange={(e) => setReopenWhy(e.target.value)} /></Field>
      </Dialog>
      <Dialog open={closing} onClose={() => setClosing(false)} title={`Close ${monthName(month)}?`}
        subtitle="Its performance is frozen as it stands now. Invoices raised or changed later will not alter it."
        footer={(
          <>
            <button type="button" className="btn-sp" onClick={() => setClosing(false)}>Cancel</button>
            <button type="button" className="btn-sp-primary" disabled={close.isPending} onClick={async () => {
              try { await close.mutateAsync(month); toast.success(`${monthName(month)} closed and frozen.`); setClosing(false); } catch (err) { toast.error(errText(err, 'Could not close the month.')); }
            }}><Lock className="h-4 w-4" /> {close.isPending ? 'Closing…' : 'Close month'}</button>
          </>
        )}>
        <p className="text-sm text-slate-600">Incentives are calculated first if they have not been. Months also close automatically a few days after they end (see Incentive Rules).</p>
      </Dialog>
    </div>
  );
}

function Total({ label, value, tone = 'text-slate-900' }: { label: string; value: number; tone?: string }) {
  return <div><p className="text-data-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p><p className={`text-base font-bold tabular-nums ${tone}`}>{inr(value)}</p></div>;
}

function RowMenu({ actions, canAdjust, onAction, onAdjust }: { actions: ActionDef[]; canAdjust: boolean; onAction: (a: ActionDef) => void; onAdjust: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" className="btn-sp !px-2 !py-1" onClick={() => setOpen((o) => !o)} aria-label="More actions"><SlidersHorizontal className="h-3.5 w-3.5" /></button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-40 mt-1 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
            {canAdjust && <button type="button" className="block w-full px-3 py-2 text-left text-[13px] hover:bg-slate-50" onClick={() => { setOpen(false); onAdjust(); }}>Adjust amount…</button>}
            {actions.map((a) => (
              <button key={a.action} type="button" className={`block w-full px-3 py-2 text-left text-[13px] hover:bg-slate-50 ${a.style === 'danger' ? 'text-rose-600' : ''}`} onClick={() => { setOpen(false); onAction(a); }}>{a.label}…</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ActionDialog({ def, items, busy, onClose, onConfirm }: {
  def: ActionDef; items: Incentive[]; busy: boolean; onClose: () => void; onConfirm: (extra: { reason?: string; transactionRef?: string }) => void;
}) {
  const [text, setText] = useState('');
  const total = items.reduce((n, i) => n + i.finalAmount, 0);
  const ok = !def.needs || text.trim().length > 0;
  return (
    <Dialog open onClose={onClose} title={`${def.label} — ${items.length === 1 ? items[0].employeeName : `${items.length} incentives`}`}
      subtitle={`${items.length === 1 ? monthName(items[0].periodKey) + ' · ' : ''}${inr(total)}`}
      footer={(
        <>
          <button type="button" className="btn-sp" onClick={onClose}>Cancel</button>
          <button type="button" className={btn(def.style === 'plain' ? 'primary' : def.style)} disabled={!ok || busy}
            onClick={() => onConfirm(def.needs === 'ref' ? { transactionRef: text.trim() } : def.needs === 'reason' ? { reason: text.trim() } : {})}>
            <CheckCircle2 className="h-4 w-4" /> {busy ? 'Working…' : def.label}
          </button>
        </>
      )}>
      {items.length > 1 && (
        <ul className="mb-3 max-h-40 overflow-y-auto rounded-lg border border-slate-100 text-[13px]">
          {items.map((i) => <li key={i._id} className="flex justify-between border-b border-slate-50 px-3 py-1.5 last:border-0"><span>{i.employeeName} · {monthName(i.periodKey)}</span><b className="tabular-nums">{inr(i.finalAmount)}</b></li>)}
        </ul>
      )}
      {def.needs === 'ref' && <Field label="Payment reference (UTR / cheque / bank ref)" hint="Saved against each incentive as proof of payment."><input className={inputCls} autoFocus value={text} onChange={(e) => setText(e.target.value)} /></Field>}
      {def.needs === 'reason' && <Field label="Reason" hint="The employee and the approvers see this in the history."><textarea className={inputCls} rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} /></Field>}
      {!def.needs && <p className="text-sm text-slate-600">{{
        submit: 'Sends it to the manager for approval.', manager_approve: 'Approves as manager and passes it to Finance to verify.',
        finance_approve: 'Confirms the amount and passes it for final approval.', approve: 'Final approval — Finance can then pay it.',
      }[def.action] || 'Confirm?'}</p>}
    </Dialog>
  );
}

function AdjustDialog({ inc, busy, onClose, onSave }: { inc: Incentive; busy: boolean; onClose: () => void; onSave: (v: { label: string; amount: number; reason: string }) => void }) {
  const [kind, setKind] = useState<'add' | 'deduct'>('add');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const n = Math.abs(Number(amount));
  const ok = label.trim() && n > 0 && reason.trim();
  return (
    <Dialog open onClose={onClose} title={`Adjust — ${inc.employeeName}`} subtitle={`${monthName(inc.periodKey)} · now ${inr(inc.finalAmount)}`}
      footer={(
        <>
          <button type="button" className="btn-sp" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-sp-primary" disabled={!ok || busy} onClick={() => onSave({ label: label.trim(), amount: kind === 'add' ? n : -n, reason: reason.trim() })}>{busy ? 'Saving…' : 'Save adjustment'}</button>
        </>
      )}>
      <div className="flex flex-col gap-4">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {(['add', 'deduct'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`flex-1 rounded-md py-1.5 text-[13px] font-semibold ${kind === k ? 'bg-white shadow-sm ' + (k === 'add' ? 'text-emerald-700' : 'text-rose-700') : 'text-slate-500'}`}>{k === 'add' ? 'Add (bonus)' : 'Deduct (penalty)'}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="What is it?"><input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={kind === 'add' ? 'Spot bonus' : 'Late payment recovery'} autoFocus /></Field>
          <Field label="Amount (₹)"><input className={inputCls} type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        </div>
        <Field label="Reason"><textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        {n > 0 && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-600">New amount: <b className="text-slate-900">{inr(Math.max(0, inc.finalAmount + (kind === 'add' ? n : -n)))}</b> (the calculated amount stays in the history)</p>}
      </div>
    </Dialog>
  );
}

export function Breakdown({ inc }: { inc: Pick<Incentive, 'breakdown' | 'finalAmount' | 'adjustments' | 'history' | 'originalAmount'> }) {
  const b = inc.breakdown;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="mb-2 text-data-xs font-semibold uppercase tracking-wide text-slate-500">How it was calculated</p>
        {b?.slab && <p className="mb-2 text-xs text-slate-500">Slab {b.slab.fromPct}%–{b.slab.toPct === null ? 'above' : `${b.slab.toPct}%`} pays {b.slab.ratePct}% on {inr(b.eligibleSales)}</p>}
        {!b?.lines?.length && <p className="text-[13px] text-slate-500">{b?.reason || 'Nothing earned this month.'}</p>}
        <table className="w-full text-[13px]">
          <tbody>
            {(b?.lines || []).map((l, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="py-1.5 pr-4 text-slate-700">{l.label}{l.note && <div className="text-data-xs text-slate-400">{l.note}</div>}</td>
                <td className={`py-1.5 text-right tabular-nums ${l.amount < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{inr(l.amount)}</td>
              </tr>
            ))}
            {b?.capped && <tr><td colSpan={2} className="py-1.5 text-xs text-amber-700">Capped at the maximum monthly incentive</td></tr>}
            <tr className="font-bold text-slate-900"><td className="pt-2">Final incentive</td><td className="pt-2 text-right tabular-nums">{inr(inc.finalAmount)}</td></tr>
          </tbody>
        </table>
        {b && !b.eligible && b.reason && b.lines?.length > 0 && <p className="mt-2 text-xs text-amber-700">{b.reason}</p>}
        {inc.adjustments?.length > 0 && <p className="mt-2 text-data-xs text-slate-500">Calculated before adjustments: {inr(inc.originalAmount)}</p>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="mb-2 text-data-xs font-semibold uppercase tracking-wide text-slate-500">History</p>
        {!(inc.history || []).length && !(inc.adjustments || []).length && <p className="text-[13px] text-slate-400">Nothing yet.</p>}
        <ol className="relative ml-1.5 border-l border-slate-200">
          {(inc.history || []).map((h, i) => (
            <li key={i} className="mb-3 ml-4 last:mb-0">
              <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500" />
              <p className="text-[13px]"><b className="text-slate-900">{STATUS_LABEL[h.status] || h.status}</b> <span className="text-slate-500">by {h.byName}</span></p>
              <p className="text-data-xs text-slate-400">{new Date(h.at).toLocaleString('en-IN')}{h.reason ? ` · “${h.reason}”` : ''}</p>
            </li>
          ))}
          {(inc.adjustments || []).map((a, i) => (
            <li key={`a${i}`} className="mb-3 ml-4 last:mb-0">
              <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500" />
              <p className="text-[13px]"><b className={a.amount < 0 ? 'text-rose-600' : 'text-emerald-700'}>{a.amount < 0 ? '−' : '+'}{inr(Math.abs(a.amount))}</b> {a.label} <span className="text-slate-500">by {a.byName}</span></p>
              <p className="text-data-xs text-slate-400">{new Date(a.at).toLocaleString('en-IN')} · “{a.reason}”</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
